import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { Button, Typography, message, Spin } from "antd";
import {
  Link,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import DocUpdateIcon from "../../assets/DocUpdateIcon.svg";
import HelpIcon from "../../assets/HelpIcon.svg";
import WPFooter from "../../components/WPFooter/WPFooter";
import {
  sendOTP,
  resendOTP,
  verifyOTP,
  getRequestStatus,
  getRequestIdFromUrl,
} from "../../api/samplesIntegrationApi";
import "./VerifyPhoneOTP.css";

const VerifyPhoneOTP = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [otp, setOtp] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [resendTimer, setResendTimer] = useState(0);
  const [requestID, setRequestID] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipArrowPx, setTooltipArrowPx] = useState(0);
  const hiddenInputRef = useRef<HTMLInputElement>(null);
  const tooltipIconRef = useRef<HTMLImageElement>(null);
  const tooltipBoxRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (showTooltip && tooltipIconRef.current && tooltipBoxRef.current) {
      const iconRect = tooltipIconRef.current.getBoundingClientRect();
      const tooltipRect = tooltipBoxRef.current.getBoundingClientRect();
      const iconCenterX = iconRect.left + iconRect.width / 2;
      const raw = iconCenterX - tooltipRect.left - 6;
      setTooltipArrowPx(Math.max(16, Math.min(tooltipRect.width - 28, raw)));
    }
  }, [showTooltip]);

  useEffect(() => {
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = "#7EC8CB";
    return () => {
      document.body.style.backgroundColor = prev;
    };
  }, []);

  useEffect(() => {
    const initializeOTP = async () => {
      const requestIdFromUrl =
        getRequestIdFromUrl() || searchParams.get("requestID");

      if (!requestIdFromUrl) {
        setError("Invalid request. No request ID provided.");
        setIsLoading(false);
        return;
      }

      setRequestID(requestIdFromUrl);

      try {
        const status = await getRequestStatus(requestIdFromUrl);
        console.log(
          "[VerifyPhoneOTP] requestStatus response:",
          JSON.stringify(status, null, 2),
        );

        if (status.isClosed) {
          navigate("/form-unavailable", {
            state: {
              reason:
                status.formStatus === "submitted" ? "submitted" : "closed",
            },
          });
          return;
        }

        if (status.maskedPhone) {
          setPhoneNumber(status.maskedPhone);
        }

        const otpResult = await sendOTP(requestIdFromUrl);
        if (otpResult.maskedPhone) {
          setPhoneNumber(otpResult.maskedPhone);
        }
        setResendTimer(60);
        message.success("Verification code sent!");
      } catch (err: any) {
        console.error("Failed to initialize OTP:", err);
        setError(err.message || "Failed to send verification code");
      } finally {
        setIsLoading(false);
        hiddenInputRef.current?.focus();
      }
    };

    initializeOTP();
  }, [navigate, searchParams]);

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setOtp(value);
  };

  const handleResend = async () => {
    if (!requestID) {
      message.error("Invalid request.");
      return;
    }

    try {
      await resendOTP(requestID);
      setResendTimer(60);
      setOtp("");
      hiddenInputRef.current?.focus();
      message.success("Verification code resent!");
    } catch (err: any) {
      message.error(err.message || "Failed to resend code.");
    }
  };

  const handleVerify = async () => {
    if (!requestID) {
      message.error("Invalid request.");
      return;
    }

    if (otp.length !== 6) {
      message.error("Please enter the complete 6-digit code.");
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await verifyOTP(requestID, otp);

      if (result.verified) {
        message.success("Phone verified successfully!");
        navigate(`/select-address?requestID=${requestID}`);
      } else {
        message.error("Invalid verification code. Please try again.");
      }
    } catch (err: any) {
      message.error(
        err.message || "Invalid verification code. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="verify-phone-container">
        <header className="verify-phone-header">
          <Link to="https://www.docupdate.io/">
            <img
              src={DocUpdateIcon}
              alt="DocUpdate"
              className="verify-phone-logo"
            />
          </Link>
        </header>
        <main className="verify-phone-main">
          <div className="verify-phone-card">
            <Spin size="large" />
            <Typography.Text
              className="verify-phone-title"
              style={{ marginTop: 16 }}
            >
              Loading...
            </Typography.Text>
          </div>
        </main>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="verify-phone-container">
        <header className="verify-phone-header">
          <Link to="https://www.docupdate.io/">
            <img
              src={DocUpdateIcon}
              alt="DocUpdate"
              className="verify-phone-logo"
            />
          </Link>
        </header>
        <main className="verify-phone-main">
          <div className="verify-phone-card">
            <Typography.Text
              className="verify-phone-title"
              style={{ color: "#ef4444" }}
            >
              {error}
            </Typography.Text>
            <Button
              type="primary"
              onClick={() => window.location.reload()}
              className="verify-phone-submit-btn"
              style={{ marginTop: 16 }}
            >
              Try Again
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="verify-phone-container">
      {/* Header */}
      <header className="verify-phone-header">
        <Link to="https://www.docupdate.io/">
          <img
            src={DocUpdateIcon}
            alt="DocUpdate"
            className="verify-phone-logo"
          />
        </Link>
      </header>

      {/* Main Content */}
      <main className="verify-phone-main">
        <div className="verify-phone-card">
          {/* Message Icon */}
          <div className="verify-phone-icon-wrapper">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z"
                stroke="#7E5AE2"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          {/* Instruction text with tooltip */}
          <div className="verify-phone-instruction-wrapper">
            <p className="verify-phone-instruction-text">
              Enter your one-time pin sent to your device to request samples and
              resources from participating manufacturers via DocUpdate.
              <span
                className="verify-phone-tooltip-trigger"
                onMouseEnter={() => {
                  if (!("ontouchstart" in window)) setShowTooltip(true);
                }}
                onMouseLeave={() => {
                  if (!("ontouchstart" in window)) setShowTooltip(false);
                }}
              >
                <img
                  ref={tooltipIconRef}
                  src={HelpIcon}
                  alt="Help"
                  className="verify-phone-instruction-icon"
                  onClick={() => setShowTooltip(!showTooltip)}
                />
              </span>
            </p>
            {showTooltip && (
              <>
                {"ontouchstart" in window && (
                  <div
                    className="otp-tooltip-overlay"
                    onClick={() => setShowTooltip(false)}
                  />
                )}
                <div className="otp-tooltip" ref={tooltipBoxRef}>
                  <div
                    className="otp-tooltip-arrow"
                    style={{ left: `${tooltipArrowPx}px` }}
                  />
                  <div className="otp-tooltip-content">
                    DocUpdate is a service that enables healthcare professionals
                    to request samples from participating pharmaceutical
                    manufacturers. To learn more about DocUpdate,{" "}
                    <a
                      href="https://www.docupdate.io/"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#fff", textDecoration: "underline" }}
                    >
                      click here
                    </a>
                    .
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Title */}
          <Typography.Text className="verify-phone-title">
            We sent a verification code to
          </Typography.Text>
          <Typography.Text className="verify-phone-number">
            {(phoneNumber || "(•••) •••-••••")
              .split("")
              .map((char, i) =>
                char === "•" ? (
                  <span key={i} className="verify-phone-mask-dot" />
                ) : char === " " ? (
                  <span key={i}>&nbsp;</span>
                ) : (
                  <span key={i}>{char}</span>
                ),
              )}
          </Typography.Text>

          {/* OTP Input - single hidden input with visual boxes */}
          <div
            className="verify-phone-otp-inputs"
            onClick={() => hiddenInputRef.current?.focus()}
          >
            <input
              ref={hiddenInputRef}
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={otp}
              onChange={handleOtpChange}
              className="verify-phone-otp-hidden-input"
              autoComplete="off"
              style={{ left: `${Math.min(otp.length, 5) * 64}px` }}
            />
            {[0, 1, 2, 3, 4, 5].map((index) => (
              <div
                key={index}
                className={`verify-phone-otp-box ${otp[index] ? "filled" : ""} ${otp.length === index ? "active" : ""}`}
              >
                {otp[index] || ""}
              </div>
            ))}
          </div>

          {/* Verify Button */}
          <Button
            type="primary"
            htmlType="button"
            onClick={handleVerify}
            loading={isSubmitting}
            className="verify-phone-submit-btn"
          >
            Verify
          </Button>

          {/* Resend Link */}
          <div className="verify-phone-resend">
            <Typography.Text className="verify-phone-resend-text">
              Didn't receive text?{" "}
            </Typography.Text>
            {resendTimer > 0 ? (
              <Typography.Text className="verify-phone-resend-timer">
                Resend in {resendTimer}s
              </Typography.Text>
            ) : (
              <Button
                type="link"
                onClick={handleResend}
                className="verify-phone-resend-link"
              >
                Click to resend
              </Button>
            )}
          </div>
        </div>
      </main>

      <WPFooter />
    </div>
  );
};

export default VerifyPhoneOTP;
